import type { CreateParameterRequest, LossType } from "@opus-dt/shared-types";
import { mappingArrayToObject } from "@/types/parameters.types";
import type { ParamForm } from "./types";

const num = (s: string): number | null => (s.trim() === "" ? null : Number(s));

export function buildPayload(
  form: ParamForm,
  name: string,
  description: string | undefined
): CreateParameterRequest {
  switch (form.category) {
    case "STATUS":
      return {
        category: "STATUS",
        name,
        description,
        source: form.source,
        statusMapping: mappingArrayToObject(form.statusMapping),
        generatesAlarm: form.generatesAlarm,
      };
    case "NUMERIC":
      return {
        category: "NUMERIC",
        name,
        description,
        unit: form.unit.trim(),
        source: form.source,
        isRestriction: form.isRestriction,
        // Spec 078
        visualRole: form.visualRole === "TEMPERATURE" ? "TEMPERATURE" : "NONE",
        limitMax: num(form.limitMax),
        limitMin: num(form.limitMin),
      };
    case "COUNTER":
      return {
        category: "COUNTER",
        name,
        description,
        unit: form.unit.trim(),
        source: form.source,
        // Spec 081 — o que o contador mede. String vazia do formulário vira ausência, nunca `null`:
        // o schema de criação é opcional, e enviar nulo faria a rota recusar por tipo.
        productionRole: form.productionRole === "" ? undefined : form.productionRole,
        productId: form.productId === "" ? undefined : form.productId,
        lossType: form.lossType === "" ? undefined : form.lossType,
        unitFactor: num(form.unitFactor) ?? undefined,
        visualRole: form.visualRole === "FLOW" ? "FLOW" : "NONE", // Spec 078
      };
    case "BOOLEAN":
      return {
        category: "BOOLEAN",
        name,
        description,
        source: form.source,
        // Spec 080 — o booleano do relé térmico: marcado, `1` para a máquina e acende a cena.
        isRestriction: form.isRestriction,
        visualRole: form.visualRole === "TEMPERATURE" ? "TEMPERATURE" : "NONE",
      };
    case "TEXT":
      return {
        category: "TEXT",
        name,
        description,
        source: form.source,
      };
    case "DURATION":
      return {
        category: "DURATION",
        name,
        description,
        unit: form.unit.trim() as "s" | "min" | "h",
        source: form.source,
      };
    default:
      throw new Error("Categoria não suportada");
  }
}

/**
 * Spec 081 — o patch de ATUALIZAÇÃO do contador, que não é o payload de criação.
 *
 * A diferença é `null` × `undefined`. No create, campo vazio é AUSÊNCIA (`undefined`) porque o
 * schema não aceita nulo. No update, `undefined` significa "não mexa" e `UpdateParameterRequest`
 * declara os três campos `.nullable()` justamente para que a tela possa LIMPÁ-LOS. Reusar o payload
 * de criação fazia "Nenhum — não separa por produto" e a equivalência apagada virarem no-ops
 * silenciosos: o toast dizia "atualizado" e o valor antigo voltava no próximo render.
 */
export interface CounterUpdatePatch {
  productId?: string | null;
  lossType?: LossType | null;
  unitFactor?: number | null;
}

export function buildCounterUpdatePatch(form: ParamForm): CounterUpdatePatch {
  if (form.category !== "COUNTER") return {};
  return {
    productId: form.productId === "" ? null : form.productId,
    lossType: form.lossType === "" ? null : form.lossType,
    unitFactor: num(form.unitFactor),
  };
}
